import { makeAutoObservable, runInAction } from 'mobx';
import { 
  adminGetServiceCenterPosts, 
  adminCreateServiceCenterPost, 
  adminDeleteServiceCenterPost,
  adminUpdateServiceCenterPost
} from '@pro-sto/api-client';
import type { ServiceCenterPostResponseDto } from '@pro-sto/api-client';

/**
 * Store для управления постами сервисного центра
 */
export class PostsStore {
  posts: ServiceCenterPostResponseDto[] = [];
  isLoading = false;
  error: string | null = null;
  private serviceCenterUuid: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Загрузить посты сервисного центра
   */
  async fetchPosts(serviceCenterUuid: string): Promise<void> {
    this.isLoading = true;
    this.error = null;
    this.serviceCenterUuid = serviceCenterUuid;

    try {
      const response = await adminGetServiceCenterPosts({ serviceCenterUuid });
      runInAction(() => {
        this.posts = response.data || [];
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Не удалось загрузить посты';
        this.isLoading = false;
      });
      console.error('Failed to fetch posts:', error);
    }
  }

  /**
   * Создать новый пост
   */
  async createPost(): Promise<boolean> {
    if (!this.serviceCenterUuid) {
      console.error('Service center UUID is not set');
      return false;
    }

    this.error = null;

    try {
      await adminCreateServiceCenterPost({ serviceCenterUuid: this.serviceCenterUuid });
      // Перезагружаем список постов после создания
      await this.fetchPosts(this.serviceCenterUuid);
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = 'Не удалось создать пост';
      });
      console.error('Failed to create post:', error);
      return false;
    }
  }

  /**
   * Удалить пост
   */
  async deletePost(postUuid: string): Promise<boolean> {
    if (!this.serviceCenterUuid) {
      console.error('Service center UUID is not set');
      return false;
    }

    this.error = null;

    try {
      await adminDeleteServiceCenterPost({ 
        serviceCenterUuid: this.serviceCenterUuid, 
        postUuid 
      });
      // Перезагружаем список постов после удаления
      await this.fetchPosts(this.serviceCenterUuid);
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = 'Не удалось удалить пост';
      });
      console.error('Failed to delete post:', error);
      return false;
    }
  }

  /**
   * Обновить доступность поста для онлайн записи
   */
  async updatePostAvailability(postUuid: string, isAvailable: boolean): Promise<boolean> {
    if (!this.serviceCenterUuid) {
      console.error('Service center UUID is not set');
      return false;
    }

    this.error = null;

    try {
      await adminUpdateServiceCenterPost({ 
        serviceCenterUuid: this.serviceCenterUuid, 
        postUuid,
        requestBody: {
          isAvailableForOnlineBooking: isAvailable
        }
      });
      
      // Обновляем локальное состояние, создавая новый массив для корректного обновления UI
      runInAction(() => {
        this.posts = this.posts.map(post => 
          post.uuid === postUuid 
            ? { ...post, isAvailableForOnlineBooking: isAvailable }
            : post
        );
      });
      
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = 'Не удалось обновить доступность поста';
      });
      console.error('Failed to update post availability:', error);
      return false;
    }
  }

  /**
   * Сбросить состояние store
   */
  reset(): void {
    this.posts = [];
    this.isLoading = false;
    this.error = null;
    this.serviceCenterUuid = null;
  }

  /**
   * Получить количество постов
   */
  get postsCount(): number {
    return this.posts.length;
  }

  /**
   * Получить пост по UUID
   */
  getPostByUuid(uuid: string): ServiceCenterPostResponseDto | undefined {
    return this.posts.find(post => post.uuid === uuid);
  }

  /**
   * Получить пост по порядковому номеру
   */
  getPostByOrderNumber(orderNumber: number): ServiceCenterPostResponseDto | undefined {
    return this.posts.find(post => post.orderNumber === orderNumber);
  }
}

export const postsStore = new PostsStore();
